import {
  AlertTriangle,
  ArrowLeftRight,
  BarChart3,
  BookOpen,
  Boxes,
  Building,
  Building2,
  Calculator,
  Calendar,
  ChefHat,
  ClipboardCheck,
  ClipboardList,
  Clock,
  Container,
  CreditCard,
  DollarSign,
  Droplet,
  Factory,
  FileBarChart,
  FileCheck,
  FileText,
  HelpCircle,
  LayoutDashboard,
  Mail,
  MessageSquare,
  Package,
  PackageCheck,
  Plus,
  Receipt,
  Scale,
  Settings,
  Shield,
  ShoppingCart,
  SlidersHorizontal,
  Target,
  TruckIcon,
  UserCheck,
  UserCircle,
  Users,
  Users2,
  Wallet,
  Warehouse,
  Workflow,
} from "lucide-react";

import { UserRole } from "~/types/user";

// Menu item type definition
export interface MenuItem {
  title: string;
  icon: React.ElementType;
  url: string;
  submenu?: MenuItem[];
  /** Render as a non-clickable section header inside a submenu (e.g. Operaciones / Reportes). */
  isSection?: boolean;
}

// Role-specific menu items configuration
export const roleMenuItems: Record<UserRole, MenuItem[]> = {
  superadmin: [
    {
      title: "Panel",
      icon: LayoutDashboard,
      url: "/",
    },
    {
      title: "Ventas",
      icon: Receipt,
      url: "#",
      submenu: [
        {
          title: "Notas de Venta",
          icon: FileText,
          url: "/sales-notes",
        },
        {
          title: "Cotizaciones",
          icon: MessageSquare,
          url: "/cotizaciones",
        },
        {
          title: "Vendedores",
          icon: Users2,
          url: "/vendedores",
        },
        {
          title: "Clientes",
          icon: Building,
          url: "/clients",
        },
        {
          title: "Aprobación de Clientes",
          icon: UserCheck,
          url: "/client-approvals",
        },
        {
          title: "Equipo Comercial",
          icon: UserCircle,
          url: "/commercial-team",
        },
        {
          title: "Metas de Venta",
          icon: Target,
          url: "/metas-de-venta",
        },
        {
          title: "Ventas por Cliente",
          icon: BarChart3,
          url: "/ventas/clientes-dashboard",
        },
      ],
    },
    {
      title: "Facturas",
      icon: FileText,
      url: "#",
      submenu: [
        {
          title: "Resumen de Facturas",
          icon: FileText,
          url: "/facturas/visualizar",
        },
        // {
        //   title: "Saldo por Cliente",
        //   icon: ClipboardList,
        //   url: "/facturas/cuentas-por-cobrar",
        // },
        {
          title: "Notas de Credito y Facturas BSALE",
          icon: CreditCard,
          url: "/facturas/bsale",
        },
        {
          title: "Cuentas por Cobrar",
          icon: DollarSign,
          url: "/deudas",
        },
        {
          title: "Recordatorios de Deuda",
          icon: Mail,
          url: "/debt-reminders",
        },
      ],
    },
    {
      title: "Pagos",
      icon: Wallet,
      url: "#",
      submenu: [
        {
          title: "Resumen de Pagos",
          icon: ClipboardList,
          url: "/payments-summary",
        },
        {
          title: "Pagos Atrasados",
          icon: Clock,
          url: "/late-payments",
        },
        {
          title: "No identificados",
          icon: HelpCircle,
          url: "/unidentified-payments",
        },
        {
          title: "Pagos Manuales",
          icon: Plus,
          url: "/manual-payments",
        },
        {
          title: "Cheques",
          icon: Receipt,
          url: "/cheques",
        },
        {
          title: "Historial de Deuda",
          icon: Calendar,
          url: "/daily-debt-snapshots",
        },
        {
          title: "Configuracion",
          icon: Settings,
          url: "/config-payments",
        },
      ],
    },
    {
      title: "Compras",
      icon: ShoppingCart,
      url: "#",
      submenu: [
        {
          title: "Órdenes de Compra",
          icon: ClipboardList,
          url: "/purchasing/orders",
        },
        {
          title: "Modificaciones de Recepción",
          icon: ClipboardCheck,
          url: "/purchasing/modificaciones-recepcion",
        },
        {
          title: "Proveedores",
          icon: Building,
          url: "/purchasing/compras/proveedores",
        },
        {
          title: "Stock Ingredientes",
          icon: Droplet,
          url: "/purchasing/stock",
        },
      ],
    },
    {
      title: "Producción",
      icon: Factory,
      url: "#",
      submenu: [
        {
          title: "Recetas",
          icon: ChefHat,
          url: "/production/recipes",
        },
        {
          title: "Aprobación de Recetas",
          icon: ClipboardCheck,
          url: "/production/recipes/aprobaciones",
        },
        {
          title: "Lotes",
          icon: Package,
          url: "/production/batches",
        },
        {
          title: "Líneas",
          icon: Workflow,
          url: "/production/assembly-lines",
        },
        {
          title: "Consumos",
          icon: Droplet,
          url: "/production/consumos",
        },
        {
          title: "Consumo por SKU",
          icon: Boxes,
          url: "/production/consumo-por-sku",
        },
        {
          title: "Ajustes de Consumo",
          icon: AlertTriangle,
          url: "/production/mermas",
        },
      ],
    },
    {
      title: "Costeo",
      icon: Calculator,
      url: "#",
      submenu: [
        {
          title: "Panel",
          icon: LayoutDashboard,
          url: "/costeo",
        },
        {
          title: "Inventario Final",
          icon: Package,
          url: "/costeo/inventario-inicial",
        },
        {
          title: "Compras",
          icon: ShoppingCart,
          url: "/costeo/compras",
        },
        {
          title: "Mov. Materia Prima",
          icon: ArrowLeftRight,
          url: "/costeo/movimiento-mp",
        },
        {
          title: "Mov. Prod. Terminado",
          icon: FileBarChart,
          url: "/costeo/movimiento-pt",
        },
        {
          title: "Conciliación",
          icon: FileCheck,
          url: "/costeo/conciliacion",
        },
        {
          title: "Variación Inventario",
          icon: Scale,
          url: "/costeo/variacion-inventario",
        },
      ],
    },
    {
      title: "Bodega",
      icon: Warehouse,
      url: "#",
      submenu: [
        {
          title: "Logística",
          icon: Warehouse,
          url: "/wms",
        },
        {
          title: "Guías de Despacho",
          icon: FileCheck,
          url: "/guias-despacho",
        },
        {
          title: "Envíos",
          icon: TruckIcon,
          url: "/despachos",
        },
        {
          title: "Productos",
          icon: Package,
          url: "/products",
        },
        {
          title: "Maquila",
          icon: Factory,
          url: "/maquila",
        },
        {
          title: "Totems",
          icon: Container,
          url: "/totems",
        },
      ],
    },
    {
      // New parallel inventory module — beta, superadmin-only. One tab with two labeled
      // sections: Operaciones (incluye Aprobaciones, que es parte del flujo de una
      // operación) y Reportes (consultas: Stock, Historial de movimientos).
      title: "Inventario (Beta)",
      icon: Boxes,
      url: "#",
      submenu: [
        { title: "Operaciones", icon: ClipboardList, url: "#", isSection: true },
        {
          title: "Operaciones",
          icon: ClipboardList,
          url: "/inventario/operaciones",
        },
        {
          title: "Aprobaciones",
          icon: ClipboardCheck,
          url: "/inventario/aprobaciones",
        },
        { title: "Productos", icon: Package, url: "#", isSection: true },
        {
          title: "Lista maestra",
          icon: Package,
          url: "/inventario/productos",
        },
        { title: "Reportes", icon: BarChart3, url: "#", isSection: true },
        {
          title: "Stock",
          icon: Boxes,
          url: "/inventario/stock",
        },
        {
          title: "Historial de movimientos",
          icon: BookOpen,
          url: "/inventario/kardex",
        },
      ],
    },
    {
      title: "Configuración",
      icon: Shield,
      url: "#",
      submenu: [
        {
          title: "Ajustes Generales",
          icon: Settings,
          url: "/admin-settings",
        },
        {
          title: "Condiciones de Venta",
          icon: Wallet,
          url: "/sales-conditions",
        },
        {
          title: "Gestión de Riesgo",
          icon: AlertTriangle,
          url: "/client-risk-management",
        },
        {
          title: "Tipos de Cambio",
          icon: ArrowLeftRight,
          url: "/exchange-rates",
        },
      ],
    },
    {
      title: "Contabilidad",
      icon: Calculator,
      url: "#",
      submenu: [
        {
          title: "Empresas",
          icon: Building2,
          url: "/contabilidad/empresas",
        },
        {
          title: "Diarios",
          icon: BookOpen,
          url: "/contabilidad/diarios",
        },
        {
          title: "Cuentas",
          icon: Calculator,
          url: "/contabilidad/cuentas",
        },
        {
          title: "Movimientos",
          icon: FileText,
          url: "/contabilidad/movimientos",
        },
        {
          title: "Presupuestos",
          icon: Wallet,
          url: "/contabilidad/presupuestos",
        },
        {
          title: "Proveedores",
          icon: Building,
          url: "/contabilidad/proveedores",
        },
        {
          title: "Facturas de Proveedor",
          icon: Receipt,
          url: "/contabilidad/facturas-proveedor",
        },
        {
          title: "Pagos a Proveedor",
          icon: Wallet,
          url: "/contabilidad/pagos-proveedor",
        },
        {
          title: "Reportes",
          icon: FileBarChart,
          url: "/contabilidad/reportes",
        },
        {
          title: "Configuracion",
          icon: Settings,
          url: "/contabilidad/configuracion",
        },
      ],
    },
  ],
  admin: [
    {
      title: "Clientes",
      icon: Building,
      url: "/clients",
    },
    {
      title: "Aprobación de Clientes",
      icon: UserCheck,
      url: "/client-approvals",
    },
    {
      title: "Notas de Venta",
      icon: FileText,
      url: "/sales-notes",
    },
    {
      title: "Metas de Venta",
      icon: Target,
      url: "/metas-de-venta",
    },
    {
      title: "Ventas por Cliente",
      icon: BarChart3,
      url: "/ventas/clientes-dashboard",
    },
    {
      title: "Compras",
      icon: ShoppingCart,
      url: "#",
      submenu: [
        {
          title: "Órdenes de Compra",
          icon: ClipboardList,
          url: "/purchasing/orders",
        },
        {
          title: "Modificaciones de Recepción",
          icon: ClipboardCheck,
          url: "/purchasing/modificaciones-recepcion",
        },
        {
          title: "Proveedores",
          icon: Building,
          url: "/purchasing/compras/proveedores",
        },
        {
          title: "Stock Ingredientes",
          icon: Droplet,
          url: "/purchasing/stock",
        },
        {
          title: "Ajustes de Inventario",
          icon: SlidersHorizontal,
          url: "/purchasing/ajustes-ingredientes",
        },
      ],
    },
    {
      title: "Producción",
      icon: Factory,
      url: "#",
      submenu: [
        {
          title: "Recetas",
          icon: ChefHat,
          url: "/production/recipes",
        },
        {
          title: "Aprobación de Recetas",
          icon: ClipboardCheck,
          url: "/production/recipes/aprobaciones",
        },
        {
          title: "Lotes",
          icon: Package,
          url: "/production/batches",
        },
        {
          title: "Líneas",
          icon: Workflow,
          url: "/production/assembly-lines",
        },
        {
          title: "Consumos",
          icon: Droplet,
          url: "/production/consumos",
        },
        {
          title: "Consumo por SKU",
          icon: Boxes,
          url: "/production/consumo-por-sku",
        },
        {
          title: "Ajustes de Consumo",
          icon: AlertTriangle,
          url: "/production/mermas",
        },
      ],
    },
    {
      title: "Costeo",
      icon: Calculator,
      url: "#",
      submenu: [
        {
          title: "Panel",
          icon: LayoutDashboard,
          url: "/costeo",
        },
        {
          title: "Inventario Final",
          icon: Package,
          url: "/costeo/inventario-inicial",
        },
        {
          title: "Compras",
          icon: ShoppingCart,
          url: "/costeo/compras",
        },
        {
          title: "Mov. Materia Prima",
          icon: ArrowLeftRight,
          url: "/costeo/movimiento-mp",
        },
        {
          title: "Mov. Prod. Terminado",
          icon: FileBarChart,
          url: "/costeo/movimiento-pt",
        },
        {
          title: "Conciliación",
          icon: FileCheck,
          url: "/costeo/conciliacion",
        },
        {
          title: "Variación Inventario",
          icon: Scale,
          url: "/costeo/variacion-inventario",
        },
      ],
    },
    {
      title: "Bodega",
      icon: Warehouse,
      url: "#",
      submenu: [
        {
          title: "Logística",
          icon: Warehouse,
          url: "/wms",
        },
        {
          title: "Guías de Despacho",
          icon: FileCheck,
          url: "/guias-despacho",
        },
        {
          title: "Maquila",
          icon: Factory,
          url: "/maquila",
        },
      ],
    },
    {
      title: "Configuración Admin",
      icon: Shield,
      url: "#",
      submenu: [
        {
          title: "Gestión de Usuarios",
          icon: Settings,
          url: "/admin-settings",
        },
        {
          title: "Snapshots Mensuales",
          icon: Calendar,
          url: "/monthly-snapshots",
        },
        {
          title: "Tipos de Cambio",
          icon: ArrowLeftRight,
          url: "/exchange-rates",
        },
      ],
    },
    {
      title: "Contabilidad",
      icon: Calculator,
      url: "#",
      submenu: [
        {
          title: "Empresas",
          icon: Building2,
          url: "/contabilidad/empresas",
        },
        {
          title: "Diarios",
          icon: BookOpen,
          url: "/contabilidad/diarios",
        },
        {
          title: "Cuentas",
          icon: Calculator,
          url: "/contabilidad/cuentas",
        },
        {
          title: "Movimientos",
          icon: FileText,
          url: "/contabilidad/movimientos",
        },
        {
          title: "Presupuestos",
          icon: Wallet,
          url: "/contabilidad/presupuestos",
        },
        {
          title: "Proveedores",
          icon: Building,
          url: "/contabilidad/proveedores",
        },
        {
          title: "Facturas de Proveedor",
          icon: Receipt,
          url: "/contabilidad/facturas-proveedor",
        },
        {
          title: "Pagos a Proveedor",
          icon: Wallet,
          url: "/contabilidad/pagos-proveedor",
        },
        {
          title: "Reportes",
          icon: FileBarChart,
          url: "/contabilidad/reportes",
        },
        {
          title: "Configuracion",
          icon: Settings,
          url: "/contabilidad/configuracion",
        },
      ],
    },
  ],
  seller: [
    {
      title: "Notas de Venta",
      icon: ClipboardList,
      url: "/sales-notes",
    },
    {
      title: "Clientes",
      icon: Users,
      url: "/clients",
    },
    {
      title: "Metas de Venta",
      icon: Target,
      url: "/metas-de-venta",
    },
    {
      title: "Facturas",
      icon: FileText,
      url: "#",
      submenu: [
        {
          title: "Visualizar",
          icon: FileText,
          url: "/facturas/visualizar",
        },
      ],
    },
  ],
  logistica: [
    {
      title: "Notas de Venta",
      icon: FileText,
      url: "/sales-notes",
    },
    {
      title: "Compras",
      icon: ShoppingCart,
      url: "#",
      submenu: [
        {
          title: "Órdenes de Compra",
          icon: ClipboardList,
          url: "/purchasing/orders",
        },
        {
          title: "Modificaciones de Recepción",
          icon: ClipboardCheck,
          url: "/purchasing/modificaciones-recepcion",
        },
        {
          title: "Proveedores",
          icon: Building,
          url: "/purchasing/compras/proveedores",
        },
        {
          title: "Stock Ingredientes",
          icon: Droplet,
          url: "/purchasing/stock",
        },
        {
          title: "Ajustes de Inventario",
          icon: SlidersHorizontal,
          url: "/purchasing/ajustes-ingredientes",
        },
      ],
    },
    {
      title: "Producción",
      icon: Factory,
      url: "#",
      submenu: [
        {
          title: "Recetas",
          icon: ChefHat,
          url: "/production/recipes",
        },
        {
          title: "Aprobación de Recetas",
          icon: ClipboardCheck,
          url: "/production/recipes/aprobaciones",
        },
        {
          title: "Lotes",
          icon: Package,
          url: "/production/batches",
        },
        {
          title: "Líneas",
          icon: Workflow,
          url: "/production/assembly-lines",
        },
        {
          title: "Consumos",
          icon: Droplet,
          url: "/production/consumos",
        },
        {
          title: "Consumo por SKU",
          icon: Boxes,
          url: "/production/consumo-por-sku",
        },
        {
          title: "Ajustes de Consumo",
          icon: AlertTriangle,
          url: "/production/mermas",
        },
      ],
    },
    {
      title: "Costeo",
      icon: Calculator,
      url: "#",
      submenu: [
        {
          title: "Panel",
          icon: LayoutDashboard,
          url: "/costeo",
        },
        {
          title: "Inventario Final",
          icon: Package,
          url: "/costeo/inventario-inicial",
        },
        {
          title: "Compras",
          icon: ShoppingCart,
          url: "/costeo/compras",
        },
        {
          title: "Mov. Materia Prima",
          icon: ArrowLeftRight,
          url: "/costeo/movimiento-mp",
        },
        {
          title: "Mov. Prod. Terminado",
          icon: FileBarChart,
          url: "/costeo/movimiento-pt",
        },
        {
          title: "Conciliación",
          icon: FileCheck,
          url: "/costeo/conciliacion",
        },
        {
          title: "Variación Inventario",
          icon: Scale,
          url: "/costeo/variacion-inventario",
        },
      ],
    },
    {
      title: "Bodega",
      icon: Warehouse,
      url: "#",
      submenu: [
        {
          title: "Logística",
          icon: Warehouse,
          url: "/wms",
        },
        {
          title: "Guías de Despacho",
          icon: FileCheck,
          url: "/guias-despacho",
        },
        {
          title: "Envíos",
          icon: TruckIcon,
          url: "/despachos",
        },
        {
          title: "Maquila",
          icon: Factory,
          url: "/maquila",
        },
        {
          title: "Totems",
          icon: Container,
          url: "/totems",
        },
      ],
    },
  ],
  cobranza: [
    {
      title: "Notas de Venta",
      icon: FileText,
      url: "/sales-notes",
    },
    {
      title: "Clientes",
      icon: Building,
      url: "/clients",
    },
    {
      title: "Facturas",
      icon: FileText,
      url: "#",
      submenu: [
        {
          title: "Visualizar",
          icon: FileText,
          url: "/facturas/visualizar",
        },
        // {
        //   title: "Cuentas por Cobrar",
        //   icon: ClipboardList,
        //   url: "/facturas/cuentas-por-cobrar",
        // },
        {
          title: "Notas de Credito y Facturas BSALE",
          icon: CreditCard,
          url: "/facturas/bsale",
        },
      ],
    },
    {
      title: "Solicitudes de Pago",
      icon: CreditCard,
      url: "/payment-requests",
    },
    {
      title: "Panel de Pagos",
      icon: ClipboardList,
      url: "/payment-dashboard",
    },
    {
      title: "Pagos",
      icon: Wallet,
      url: "#",
      submenu: [
        {
          title: "Resumen de Pagos",
          icon: ClipboardList,
          url: "/payments-summary",
        },
        {
          title: "Pagos Atrasados",
          icon: Clock,
          url: "/late-payments",
        },
        {
          title: "No identificados",
          icon: HelpCircle,
          url: "/unidentified-payments",
        },
        {
          title: "Pagos Manuales",
          icon: Plus,
          url: "/manual-payments",
        },
        {
          title: "Cheques",
          icon: Receipt,
          url: "/cheques",
        },
        {
          title: "Historial de Deuda",
          icon: Calendar,
          url: "/daily-debt-snapshots",
        },
        {
          title: "Configuracion",
          icon: Settings,
          url: "/config-payments",
        },
      ],
    },
  ],
  facturacion: [
    {
      title: "Notas de Venta",
      icon: FileText,
      url: "/sales-notes",
    },
    {
      title: "Clientes",
      icon: Building,
      url: "/clients",
    },
    {
      title: "Facturas",
      icon: FileText,
      url: "#",
      submenu: [
        {
          title: "Visualizar",
          icon: FileText,
          url: "/facturas/visualizar",
        },
        {
          title: "Cuentas por Cobrar",
          icon: DollarSign,
          url: "/deudas",
        },
        {
          title: "Notas de Credito y Facturas BSALE",
          icon: CreditCard,
          url: "/facturas/bsale",
        },
      ],
    },
    {
      title: "Guías de Despacho",
      icon: FileCheck,
      url: "/guias-despacho",
    },
  ],
  risk: [
    {
      title: "Notas de Venta",
      icon: FileText,
      url: "/sales-notes",
    },
    {
      title: "Evaluación de Riesgo",
      icon: AlertTriangle,
      url: "/risk-assessment",
    },
    {
      title: "Clientes",
      icon: Users,
      url: "/clients",
    },
    {
      title: "Gestión de Riesgo",
      icon: Shield,
      url: "/client-risk-management",
    },
    {
      title: "Equipo Comercial",
      icon: UserCircle,
      url: "/commercial-team",
    },
  ],
  transportista: [
    {
      title: "Todas las Notas de Venta",
      icon: FileText,
      url: "/sales-notes",
    },
  ],
  production: [
    {
      title: "Lotes de Producción",
      icon: Package,
      url: "/production/batches",
    },
    {
      title: "Maquila",
      icon: Factory,
      url: "#",
      submenu: [
        {
          title: "Registrar Consumo",
          icon: Droplet,
          url: "/maquila/consumptions/new",
        },
        {
          title: "Consumos de Maquila",
          icon: BookOpen,
          url: "/maquila/consumptions",
        },
        {
          title: "Recetas de Maquila",
          icon: ChefHat,
          url: "/maquila/recipes",
        },
      ],
    },
  ],
  bodega: [
    {
      title: "Bodega",
      icon: Warehouse,
      url: "#",
      submenu: [
        {
          title: "Logística",
          icon: Warehouse,
          url: "/wms",
        },
        {
          title: "Maquila",
          icon: Factory,
          url: "/maquila",
        },
      ],
    },
  ],
  "produccion-admin": [
    {
      title: "Compras",
      icon: ShoppingCart,
      url: "#",
      submenu: [
        {
          title: "Órdenes de Compra",
          icon: ClipboardList,
          url: "/purchasing/orders",
        },
        {
          title: "Modificaciones de Recepción",
          icon: ClipboardCheck,
          url: "/purchasing/modificaciones-recepcion",
        },
        {
          title: "Ingreso",
          icon: PackageCheck,
          url: "/purchasing/ingreso",
        },
        {
          title: "Proveedores",
          icon: Building,
          url: "/purchasing/compras/proveedores",
        },
        {
          title: "Stock Ingredientes",
          icon: Droplet,
          url: "/purchasing/stock",
        },
        {
          title: "Traslados",
          icon: ArrowLeftRight,
          url: "/purchasing/traslados",
        },
        {
          title: "Ajustes de Inventario",
          icon: SlidersHorizontal,
          url: "/purchasing/ajustes-ingredientes",
        },
      ],
    },
    {
      title: "Producción",
      icon: Factory,
      url: "#",
      submenu: [
        {
          title: "Recetas",
          icon: ChefHat,
          url: "/production/recipes",
        },
        {
          title: "Aprobación de Recetas",
          icon: ClipboardCheck,
          url: "/production/recipes/aprobaciones",
        },
        {
          title: "Lotes",
          icon: Package,
          url: "/production/batches",
        },
        {
          title: "Líneas",
          icon: Workflow,
          url: "/production/assembly-lines",
        },
        {
          title: "Consumos",
          icon: Droplet,
          url: "/production/consumos",
        },
        {
          title: "Consumo por SKU",
          icon: Boxes,
          url: "/production/consumo-por-sku",
        },
        {
          title: "Ajustes de Consumo",
          icon: AlertTriangle,
          url: "/production/mermas",
        },
      ],
    },
    {
      title: "Costeo",
      icon: Calculator,
      url: "#",
      submenu: [
        {
          title: "Panel",
          icon: LayoutDashboard,
          url: "/costeo",
        },
        {
          title: "Inventario Final",
          icon: Package,
          url: "/costeo/inventario-inicial",
        },
        {
          title: "Compras",
          icon: ShoppingCart,
          url: "/costeo/compras",
        },
        {
          title: "Mov. Materia Prima",
          icon: ArrowLeftRight,
          url: "/costeo/movimiento-mp",
        },
        {
          title: "Mov. Prod. Terminado",
          icon: FileBarChart,
          url: "/costeo/movimiento-pt",
        },
        {
          title: "Conciliación",
          icon: FileCheck,
          url: "/costeo/conciliacion",
        },
        {
          title: "Variación Inventario",
          icon: Scale,
          url: "/costeo/variacion-inventario",
        },
      ],
    },
    {
      title: "Maquila",
      icon: Factory,
      url: "#",
      submenu: [
        {
          title: "Registrar Consumo",
          icon: Droplet,
          url: "/maquila/consumptions/new",
        },
        {
          title: "Consumos de Maquila",
          icon: BookOpen,
          url: "/maquila/consumptions",
        },
        {
          title: "Recetas de Maquila",
          icon: ChefHat,
          url: "/maquila/recipes",
        },
      ],
    },
  ],
  // External read-only fiscal advisor: accounting reports only.
  "asesor-fiscal": [
    {
      title: "Contabilidad",
      icon: Calculator,
      url: "#",
      submenu: [
        {
          title: "Reportes",
          icon: FileBarChart,
          url: "/contabilidad/reportes",
        },
        {
          title: "Libro Diario",
          icon: BookOpen,
          url: "/contabilidad/reportes/libro-diario",
        },
        {
          title: "Libro Mayor",
          icon: BookOpen,
          url: "/contabilidad/reportes/libro-mayor",
        },
        {
          title: "Libro de Compras",
          icon: Receipt,
          url: "/contabilidad/reportes/libro-compras",
        },
        {
          title: "Balance 8 Columnas",
          icon: Scale,
          url: "/contabilidad/reportes/balance-8-columnas",
        },
        {
          title: "Estado de Situación",
          icon: FileBarChart,
          url: "/contabilidad/reportes/estado-situacion",
        },
        {
          title: "Estado de Resultados",
          icon: FileBarChart,
          url: "/contabilidad/reportes/estado-resultados",
        },
        {
          title: "Presupuesto vs Real",
          icon: Wallet,
          url: "/contabilidad/reportes/presupuesto-vs-real",
        },
        {
          title: "Cuentas por Pagar",
          icon: Wallet,
          url: "/contabilidad/reportes/cuentas-por-pagar",
        },
        {
          title: "Validación CxC",
          icon: ClipboardCheck,
          url: "/contabilidad/reportes/validacion-cxc",
        },
        {
          title: "Reconciliación CxP",
          icon: ClipboardCheck,
          url: "/contabilidad/reportes/reconciliacion-cxp",
        },
      ],
    },
  ],
};

/**
 * Top-level sidebar section titles a role can see. Used to preview which
 * modules/tabs a role unlocks when assigning it in the user dialogs.
 */
export const getRoleModuleTitles = (role: UserRole): string[] =>
  (roleMenuItems[role] ?? []).map((item) => item.title);
